import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';

const SchoolFeesReport = ({ data, formatCurrency }) => {
  if (!data) return null;

  const { summary, breakdown, students } = data;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        School Fees Report
      </Typography>
      
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Students
                  </Typography>
                  <Typography variant="h4" component="div">
                    {summary.totalStudents}
                  </Typography>
                </Box>
                <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Fees Due
                  </Typography>
                  <Typography variant="h4" component="div" color="primary">
                    {formatCurrency(summary.totalFeesDue)}
                  </Typography>
                </Box>
                <AccountBalanceIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Amount Collected (All Time)
                  </Typography>
                  <Typography variant="h4" component="div" color="success.main">
                    {formatCurrency(summary.totalAmountPaid)}
                  </Typography>
                  {summary.totalAmountPaidInPeriod > 0 && (
                    <Typography variant="caption" color="textSecondary">
                      {formatCurrency(summary.totalAmountPaidInPeriod)} in period
                    </Typography>
                  )}
                </Box>
                <ReceiptIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Outstanding
                  </Typography>
                  <Typography variant="h4" component="div" color="error.main">
                    {formatCurrency(summary.totalOutstanding)}
                  </Typography>
                </Box>
                <TrendingUpIcon color="error" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Discounts Given
                  </Typography>
                  <Typography variant="h4" component="div" color="warning.main">
                    {formatCurrency(summary.totalDiscounts || 0)}
                  </Typography>
                  {(summary.totalDiscountsInPeriod || 0) > 0 && (
                    <Typography variant="caption" color="textSecondary">
                      {formatCurrency(summary.totalDiscountsInPeriod || 0)} in period
                    </Typography>
                  )}
                </Box>
                <TrendingDownIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Period Summary */}
      {summary.period && (
        <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Period: {new Date(summary.period.start).toLocaleDateString()} - {new Date(summary.period.end).toLocaleDateString()}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Amount Collected in Period
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {formatCurrency(summary.totalAmountPaidInPeriod)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Period Collection Rate
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {summary.periodCollectionRate ? summary.periodCollectionRate.toFixed(1) : '0.0'}%
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Discounts Given in Period
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {formatCurrency(summary.totalDiscountsInPeriod || 0)}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Collection Rate */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Overall Collection Rate (All Time)
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <LinearProgress 
              variant="determinate" 
              value={summary.collectionRate} 
              sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
            />
            <Typography variant="h6" color="primary">
              {summary.collectionRate.toFixed(1)}%
            </Typography>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {summary.paidStudents} of {summary.totalStudents} students have paid their fees
          </Typography>
        </CardContent>
      </Card>

      {/* Breakdown by Class */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Breakdown by Class
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Class</strong></TableCell>
                  <TableCell align="right"><strong>Students</strong></TableCell>
                  <TableCell align="right"><strong>Fees Due</strong></TableCell>
                  <TableCell align="right"><strong>Collected (All)</strong></TableCell>
                  <TableCell align="right"><strong>Collected (Period)</strong></TableCell>
                  <TableCell align="right"><strong>Outstanding</strong></TableCell>
                  <TableCell align="right"><strong>Collection Rate</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(breakdown.byClass).map(([className, classData]) => {
                  // Calculate period payments for this class
                  const classStudents = data.students.filter(s => s.class === className);
                  const periodPayments = classStudents.reduce((sum, s) => sum + (s.amountPaidInPeriod || 0), 0);
                  
                  return (
                    <TableRow key={className}>
                      <TableCell>{className}</TableCell>
                      <TableCell align="right">{classData.totalStudents}</TableCell>
                      <TableCell align="right">{formatCurrency(classData.totalFeesDue)}</TableCell>
                      <TableCell align="right">{formatCurrency(classData.totalAmountPaid)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="primary.main" fontWeight="bold">
                          {formatCurrency(periodPayments)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatCurrency(classData.totalOutstanding)}</TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={`${((classData.totalAmountPaid / classData.totalFeesDue) * 100).toFixed(1)}%`}
                          color={classData.totalAmountPaid / classData.totalFeesDue >= 0.8 ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Breakdown by Term */}
      {breakdown.byTerm && Object.keys(breakdown.byTerm).length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Breakdown by Term
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Term</strong></TableCell>
                    <TableCell align="right"><strong>Students</strong></TableCell>
                    <TableCell align="right"><strong>Fees Due</strong></TableCell>
                    <TableCell align="right"><strong>Collected (All)</strong></TableCell>
                    <TableCell align="right"><strong>Collected (Period)</strong></TableCell>
                    <TableCell align="right"><strong>Outstanding</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(breakdown.byTerm).map(([term, data]) => (
                    <TableRow key={term}>
                      <TableCell>{term}</TableCell>
                      <TableCell align="right">{data.totalStudents}</TableCell>
                      <TableCell align="right">{formatCurrency(data.totalFeesDue)}</TableCell>
                      <TableCell align="right">{formatCurrency(data.totalAmountPaid)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="primary.main" fontWeight="bold">
                          {formatCurrency(data.totalAmountPaidInPeriod)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatCurrency(data.totalOutstanding)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Payment Status Breakdown */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Payment Status Breakdown
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="success.main" variant="h4">
                    {breakdown.byPaymentStatus.fullyPaid.count}
                  </Typography>
                  <Typography color="textSecondary">
                    Fully Paid Students
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    {formatCurrency(breakdown.byPaymentStatus.fullyPaid.totalPaid)} collected
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="warning.main" variant="h4">
                    {breakdown.byPaymentStatus.partiallyPaid.count}
                  </Typography>
                  <Typography color="textSecondary">
                    Partially Paid Students
                  </Typography>
                  <Typography variant="body2" color="warning.main">
                    {formatCurrency(breakdown.byPaymentStatus.partiallyPaid.totalOutstanding)} outstanding
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography color="error.main" variant="h4">
                    {breakdown.byPaymentStatus.notPaid.count}
                  </Typography>
                  <Typography color="textSecondary">
                    Not Paid Students
                  </Typography>
                  <Typography variant="body2" color="error.main">
                    {formatCurrency(breakdown.byPaymentStatus.notPaid.totalOutstanding)} outstanding
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Top Outstanding Students */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Top Outstanding Students
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Student</strong></TableCell>
                  <TableCell><strong>Class</strong></TableCell>
                  <TableCell align="right"><strong>Fees Due</strong></TableCell>
                  <TableCell align="right"><strong>Paid</strong></TableCell>
                  <TableCell align="right"><strong>Outstanding</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {students
                  .filter(student => student.outstandingBalance > 0)
                  .sort((a, b) => b.outstandingBalance - a.outstandingBalance)
                  .slice(0, 10)
                  .map((student, index) => (
                    <TableRow key={student.studentId}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {student.studentName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            ID: {student.studentIdNumber}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{student.class}</TableCell>
                      <TableCell align="right">{formatCurrency(student.totalFeesDue)}</TableCell>
                      <TableCell align="right">{formatCurrency(student.amountPaid)}</TableCell>
                      <TableCell align="right">
                        <Typography color="error.main" fontWeight="bold">
                          {formatCurrency(student.outstandingBalance)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={student.isFullyPaid ? "Paid" : "Outstanding"}
                          color={student.isFullyPaid ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SchoolFeesReport; 